import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import RoutePage from './RoutePage';
import { withContextProvider } from '../../hoc';
import { useToasts } from '../../hooks';
import { ToastProvider, DialogsProvider } from '../../providers';
import { RoutesContext } from '../../context';
import { addSiteToRoute, removeStopRoute, reorderRouteStops } from '../../utils/api/routes';
import { handleResponse } from '../../utils/api/errorHandler';
import { transformRouteStopToCamelCase } from '../../utils/routeStops';
import { SITE_DEFAULT_SEARCH_PARAMS } from '../../constants/sites';
import cleanUpObject from '../../utils/cleanUpObject';
import { getSearchObjectFromUrlParams } from '../../utils/urlToSearchParams';
import useUrlSearchParams from '../../hooks/useUrlSearchParams';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { cleanParams } from '../../utils/api/maps';
import { geth2aFormat } from '../../utils/api/site';

const MAX_ROUTES_ALLOWED = 25;

const normalizeRoute = (route) => {
  if (!route) return null;
  if (route.routeId || route.organizationId || route.stops) return route;
  if (route.route_stops || route.organization_id) return transformRouteStopToCamelCase(route);
  return route;
};

const RoutePageWrapper = ({ organizationId = null, routeId = null }) => {
  const { showToast } = useToasts();
  const { removeFromLocalStorage } = useLocalStorage();
  const [sites, setSites] = useState([]);
  const [currentRoute, setCurrentRoute] = useState(null);
  const [vamosMarkersRef, setVamosMarkersRefs] = useState([]);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [isSearch, setIsSearch] = useState(false);

  const [sort] = useUrlSearchParams('sort', 'name'); // Track sorting parameters in URL
  const [ordering] = useUrlSearchParams('ordering', 'asc'); // Default to 'asc'

  // Get the latest searchParams by merging SITE_DEFAULT_SEARCH_PARAMS,
  // urlSearchParams that reads from url, organization id, and limit.
  const limit = SITE_DEFAULT_SEARCH_PARAMS.limit || 20;

  const currentPageURL = window.location.search;
  const urlSearchParams = new URLSearchParams(currentPageURL);
  const parseQueryParams = () => {
    const searchParamsObject = getSearchObjectFromUrlParams(urlSearchParams);
    return {
      ...SITE_DEFAULT_SEARCH_PARAMS,
      ...searchParamsObject,
      organization_id: organizationId,
      limit,
      sort: sort || '',
      ordering: ordering || 'asc',
    };
  };
  const [searchParams, setSearchParams] = useState(parseQueryParams);
  const [activeParams, setActiveParams] = useState(searchParams);

  // Determine if any search params
  useEffect(() => {
    if (isSearchExpanded) return; // only update when advanced search modal is closed
    const currParams = cleanParams(activeParams);
    setIsSearch(currParams && Object.keys(currParams).length > 0);
  }, [activeParams, isSearchExpanded]);

  const updateSearchParamsAndUrl = (params) => {
    setSearchParams(params);
    cleanUpObject(params);
    const newURL = new URLSearchParams(params).toString();
    const path = `/routes/${routeId}/edit?mode=map&${newURL}`;
    window.history.replaceState({ path }, '', path);
  };

  const search = () => {
    // Update searchParams with the reset offset
    const updatedSearchParams = { ...activeParams, organization_id: organizationId };
    updateSearchParamsAndUrl(updatedSearchParams);
  };

  const updateRouteState = useCallback((route) => {
    const routeUpdate = normalizeRoute(route);
    if (routeUpdate) setCurrentRoute(routeUpdate);
  }, []);

  const addStop = useCallback(
    async (site, setLoading, setSelectedMarker, setIsInitialRendering) => {
      if (!currentRoute || !organizationId || !routeId) return;
      try {
        setLoading(true);
        const stop = {
          site_id: site.id,
          route_id: routeId,
          location_id: site.location_id,
          site_name: site.name,
          position: 1,
          distance: 0,
          time: 0,
        };

        if ((currentRoute.stops || []).length >= MAX_ROUTES_ALLOWED) {
          showToast({ message: `Maximum ${MAX_ROUTES_ALLOWED} stops allowed`, type: 'error' });
          return;
        }

        const response = await addSiteToRoute(organizationId, routeId, stop);
        const data = await handleResponse(response);
        if (data) {
          updateRouteState(data.route || data);
          setSelectedMarker(undefined);
          showToast({ message: 'Stop added successfully', type: 'success' });
        }
      } catch (error) {
        showToast({ message: error.message, type: 'error' });
      } finally {
        setLoading(false);
        setIsInitialRendering?.(false);
      }
    }, [currentRoute, organizationId, routeId, showToast, updateRouteState],
  );

  const removeStop = useCallback(async (stopId) => {
    if (!organizationId || !routeId) return;
    try {
      const data = {
        organization_id: organizationId,
      };

      const response = await removeStopRoute(routeId, stopId, data);
      const responseData = await handleResponse(response);
      if (responseData) {
        updateRouteState(responseData.route || responseData);
        showToast({ message: 'Stop removed successfully', type: 'success' });
      }
    } catch (error) {
      showToast({ message: error.message, type: 'error' });
    }
  }, [organizationId, routeId, showToast, updateRouteState]);

  const reorderStops = useCallback(async (stopId, newPosition) => {
    if (!organizationId || !routeId) return null;
    const response = await reorderRouteStops(routeId, stopId, organizationId, newPosition);
    const data = await handleResponse(response);
    if (data) {
      updateRouteState(data.route || data);
      showToast({ message: 'Stops reordered successfully', type: 'success' });
    }
    return data;
  }, [organizationId, routeId, showToast, updateRouteState]);

  const fetchRouteData = useCallback(() => {
    if (!organizationId || !routeId) return;

    const params = {
      ...searchParams,
      organization_id: organizationId,
      h2a: geth2aFormat(searchParams.h2a),
    };

    cleanUpObject(params);

    const queryString = new URLSearchParams(params).toString();

    fetch(`/api/v3/routes/${routeId}/edit?${queryString}`)
      .then(handleResponse)
      .then((data) => {
        const sitesArray = data?.sitesArray || [];
        setSites(sitesArray);
        if (isSearch && sitesArray.length === 0) {
          showToast({
            message: 'No sites found matching the search criteria.',
            type: 'info',
          });
        }
        const routeSelected = normalizeRoute(data?.routeSelected || data?.route || data);
        setCurrentRoute(routeSelected);
      })
      .catch((error) => showToast({ message: error.message, type: 'error' }));
  }, [organizationId, routeId, searchParams, isSearch, showToast]);

  useEffect(() => {
    fetchRouteData();

    // storage event is browser-default cross-tab only: it won't fire in the same tab that set the value.
    const handleStorageChange = (e) => {
      if (e.key !== 'site-location-changed' || e.newValue !== 'true') return;
      removeFromLocalStorage('site-location-changed');
      fetchRouteData();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [fetchRouteData, removeFromLocalStorage]);

  const contextValue = useMemo(() => ({
    sites,
    currentRoute,
    updateRouteState,
    addStop,
    removeStop,
    reorderStops,
    setVamosMarkersRefs,
    vamosMarkersRef,
  }), [sites, currentRoute, updateRouteState, addStop, removeStop, reorderStops, vamosMarkersRef]);

  return (
    <DialogsProvider>
      <RoutesContext.Provider value={contextValue}>
        <RoutePage
          currentRoute={currentRoute}
          sites={sites}
          addStop={addStop}
          updateRouteState={updateRouteState}
          removeStop={removeStop}
          organizationId={organizationId}
          activeParams={activeParams}
          setActiveParams={setActiveParams}
          isSearch={isSearch}
          setIsSearch={setIsSearch}
          search={search}
          updateSearchParamsAndUrl={updateSearchParamsAndUrl}
          searchParams={searchParams}
          isSearchExpanded={isSearchExpanded}
          setIsSearchExpanded={setIsSearchExpanded}
        />
      </RoutesContext.Provider>
    </DialogsProvider>
  );
};

RoutePageWrapper.propTypes = {
  organizationId: PropTypes.number,
  routeId: PropTypes.number,
};

export default withContextProvider(ToastProvider)(RoutePageWrapper);
